import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, SkipForward, X, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

interface TourStep {
  route: string;
  title: string;
  description: string;
  highlight?: string;
}

const TOUR_FLOWS: Record<string, { label: string; steps: TourStep[] }> = {
  core: {
    label: 'Core Dating Experience',
    steps: [
      { route: '/dashboard', title: 'Compatibility Dashboard', description: 'Your AI matchmaker reviews profiles for you and delivers Compatibility Reports — no swiping. See your Financial DNA, match queue, and stats.', highlight: '[data-testid="dashboard-page"]' },
      { route: '/matches', title: 'Your Matches', description: 'View all mutual matches with compatibility scores. Click any profile to see detailed info, or use the 3-dot menu for actions.', highlight: '[data-testid="matches-page"]' },
      { route: '/chats', title: 'Conversations', description: 'Chat with your matches in real-time. Use budget templates ($) and Money Talk Starters to spark meaningful conversations.', highlight: '[data-testid="chats-page"]' },
    ],
  },
  premium: {
    label: 'Premium vs Free',
    steps: [
      { route: '/subscription', title: 'Your Subscription', description: 'View your current plan, billing history, and available upgrades. Cancel or downgrade anytime.', highlight: '[data-testid="subscription-page"]' },
      { route: '/premium', title: 'Premium Plans', description: 'Compare Free, Premium ($12.99/mo), and Elite ($39.99/mo) tiers. Upgrade with one click via Stripe Checkout.', highlight: '[data-testid="premium-page"]' },
    ],
  },
  dates: {
    label: 'Date Planning & Bookings',
    steps: [
      { route: '/date-plan', title: 'Plan a Date', description: 'Create date plans with your matches. Set activity, budget, venue, and split type. Both partners confirm before it\'s locked in.', highlight: '[data-testid="date-plan-page"]' },
      { route: '/bookings', title: 'Bookings', description: 'Browse restaurant reservations (OpenTable), experiences (Viator), and flower delivery (1-800-Flowers). Track confirmed bookings.', highlight: '[data-testid="bookings-page"]' },
    ],
  },
  profile: {
    label: 'Profile & Fair Score',
    steps: [
      { route: '/profile', title: 'Your Profile', description: 'View your Fair Partner Score, verified badge, interests, and date history. Edit your profile to update prompts and photos.', highlight: '[data-testid="profile-page"]' },
      { route: '/settings', title: 'Settings', description: 'Manage notifications, privacy, account security, and more.', highlight: '[data-testid="settings-page"]' },
    ],
  },
};

export function DemoWalkthroughButton() {
  const [showMenu, setShowMenu] = useState(false);
  const [activeTour, setActiveTour] = useState<string | null>(null);
  const [stepIdx, setStepIdx] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const currentFlow = activeTour ? TOUR_FLOWS[activeTour] : null;
  const currentStep = currentFlow?.steps[stepIdx];
  const totalSteps = currentFlow?.steps.length || 0;

  const startTour = useCallback((flowKey: string) => {
    setActiveTour(flowKey);
    setStepIdx(0);
    setShowMenu(false);
    const firstRoute = TOUR_FLOWS[flowKey].steps[0].route;
    navigate(firstRoute);
  }, [navigate]);

  const goNext = useCallback(() => {
    if (!currentFlow) return;
    if (stepIdx < totalSteps - 1) {
      const nextIdx = stepIdx + 1;
      setStepIdx(nextIdx);
      navigate(currentFlow.steps[nextIdx].route);
    } else {
      setActiveTour(null);
      setStepIdx(0);
    }
  }, [currentFlow, stepIdx, totalSteps, navigate]);

  const goPrev = useCallback(() => {
    if (!currentFlow || stepIdx <= 0) return;
    const prevIdx = stepIdx - 1;
    setStepIdx(prevIdx);
    navigate(currentFlow.steps[prevIdx].route);
  }, [currentFlow, stepIdx, navigate]);

  const endTour = useCallback(() => {
    setActiveTour(null);
    setStepIdx(0);
  }, []);

  // Sync step when route changes externally
  useEffect(() => {
    if (!currentFlow) return;
    const matchingIdx = currentFlow.steps.findIndex(s => location.pathname.startsWith(s.route));
    if (matchingIdx >= 0 && matchingIdx !== stepIdx) {
      setStepIdx(matchingIdx);
    }
  }, [location.pathname, currentFlow, stepIdx]);

  return (
    <>
      {/* Floating trigger button */}
      {!activeTour && (
        <button
          data-testid="demo-walkthrough-btn"
          onClick={() => setShowMenu(!showMenu)}
          className="fixed bottom-20 right-4 z-40 w-10 h-10 rounded-full bg-dutch-teal text-white shadow-lg shadow-teal-200/50 flex items-center justify-center hover:scale-110 transition-transform"
          title="Demo Walkthrough"
        >
          <Sparkles size={18} />
        </button>
      )}

      {/* Tour selection menu */}
      <AnimatePresence>
        {showMenu && !activeTour && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-32 right-4 z-50 w-64 bg-white rounded-2xl shadow-2xl border border-neutral-100 overflow-hidden"
          >
            <div className="p-4 border-b border-neutral-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Play size={14} className="text-dutch-teal" />
                  <p className="text-sm font-heading font-bold text-neutral-900">Demo Walkthrough</p>
                </div>
                <button onClick={() => setShowMenu(false)} className="text-neutral-400 hover:text-neutral-600">
                  <X size={14} />
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Choose a guided tour below</p>
            </div>
            <div className="p-2">
              {Object.entries(TOUR_FLOWS).map(([key, flow]) => (
                <button
                  key={key}
                  data-testid={`tour-${key}`}
                  onClick={() => startTour(key)}
                  className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-neutral-50 transition-colors flex items-center justify-between group"
                >
                  <span className="text-xs font-medium text-neutral-700">{flow.label}</span>
                  <ChevronRight size={12} className="text-neutral-300 group-hover:text-dutch-teal transition-colors" />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active tour overlay */}
      <AnimatePresence>
        {activeTour && currentStep && (
          <motion.div
            key={`step-${stepIdx}`}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            className="fixed bottom-20 left-4 right-4 z-50 bg-white rounded-2xl shadow-2xl border border-dutch-teal/20 overflow-hidden"
          >
            {/* Progress bar */}
            <div className="h-1 bg-neutral-100">
              <div
                className="h-full bg-dutch-teal transition-all duration-300"
                style={{ width: `${((stepIdx + 1) / totalSteps) * 100}%` }}
              />
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <p className="text-[10px] text-dutch-teal font-semibold uppercase tracking-wider">
                    {currentFlow?.label} &middot; Step {stepIdx + 1}/{totalSteps}
                  </p>
                  <p className="text-sm font-heading font-bold text-neutral-900 mt-0.5">{currentStep.title}</p>
                </div>
                <button onClick={endTour} className="text-neutral-400 hover:text-neutral-600 shrink-0" data-testid="tour-close-btn">
                  <X size={16} />
                </button>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed mb-3">{currentStep.description}</p>
              <div className="flex items-center gap-2">
                {stepIdx > 0 && (
                  <Button size="sm" variant="outline" className="rounded-xl text-xs h-8" onClick={goPrev} data-testid="tour-prev-btn">
                    <ChevronLeft size={12} className="mr-1" /> Back
                  </Button>
                )}
                <Button
                  size="sm"
                  className="flex-1 rounded-xl text-xs h-8 bg-dutch-teal hover:bg-dutch-teal/90 text-white"
                  onClick={goNext}
                  data-testid="tour-next-btn"
                >
                  {stepIdx < totalSteps - 1 ? (
                    <>Next <ChevronRight size={12} className="ml-1" /></>
                  ) : (
                    'Finish Tour'
                  )}
                </Button>
                {stepIdx < totalSteps - 1 && (
                  <Button size="sm" variant="ghost" className="rounded-xl text-xs h-8 text-neutral-400" onClick={endTour} data-testid="tour-skip-btn">
                    <SkipForward size={12} className="mr-1" /> Skip
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
